import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import {
  DataCell,
  DataRow,
  DataTable,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@breakingthelines/design-system";
import { ordinal } from "@breakingthelines/viz/stats";

import type {
  ArchetypePreviewState,
  PreviewCompetition,
  PreviewGroupState,
} from "@/hooks/use-archetype-preview";
import { searchEntities, type ArchetypePreviewEntity } from "@/lib/admin-api";

import {
  entityAdminHref,
  formatSpearman,
  percentileTextColour,
  positionPlural,
} from "./archetype-preview-model";
import styles from "./archetype-preview.module.css";

const SEARCH_MIN = 2;
const SEARCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

/**
 * The preview beside the editor form (profile-spec.md §5): a competition and
 * season, then for each home position the top ten fits and the overlap with
 * every published archetype sharing that position. It scores the form as
 * typed; nothing here writes.
 */
export function ArchetypePreview({
  state,
  hasHomePositions,
}: {
  state: ArchetypePreviewState;
  hasHomePositions: boolean;
}) {
  const seasons = state.seasons?.seasons ?? [];
  const seasonLabel = (id: string | null) =>
    seasons.find((s) => s.id === id)?.label ?? (state.seasonsLoading ? "Loading" : "");

  let line: string | null = null;
  if (state.seasonsError) line = "Seasons unavailable";
  else if (state.competition && !state.seasonsLoading && seasons.length === 0) {
    line = `No seasons for ${state.competition.name || "this competition"}`;
  } else if (hasHomePositions && !state.enabled) line = "No preview for an incomplete definition";

  return (
    <section className={styles.preview} aria-label="Preview">
      <h2 className={styles.title}>Preview</h2>
      <div className={styles.controls}>
        <CompetitionPicker
          value={state.competition}
          loading={state.competitionLoading}
          onSelect={state.setCompetition}
        />
        <div className={styles.field}>
          <span className={styles.label}>Season</span>
          <Select
            value={state.seasonId || null}
            disabled={seasons.length === 0}
            onValueChange={(value: string | null) => {
              if (value) state.setSeasonId(value);
            }}
          >
            <SelectTrigger aria-label="Season" className={styles.seasonTrigger}>
              <SelectValue>{(value: string | null) => seasonLabel(value)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {seasons.map((season) => (
                <SelectItem key={season.id} value={season.id}>
                  {season.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {line ? <p className={styles.muted}>{line}</p> : null}

      {state.enabled && state.seasonId
        ? state.groups.map((group) => (
            <PreviewGroup key={group.position} group={group} eventOnly={state.eventOnly} />
          ))
        : null}
    </section>
  );
}

function EntityName({ entity }: { entity: ArchetypePreviewEntity | null }) {
  if (!entity?.id) return null;
  return (
    <Link className={styles.entity} to={entityAdminHref(entity.id)}>
      {entity.name || entity.id}
    </Link>
  );
}

function PreviewGroup({ group, eventOnly }: { group: PreviewGroupState; eventOnly: boolean }) {
  const plural = positionPlural(group.position);
  const lower = plural.toLowerCase();
  const preview = group.preview;
  const headingId = useId();

  return (
    <section
      className={clsx(styles.group, group.refreshing && styles.refreshing)}
      aria-labelledby={headingId}
      aria-busy={group.refreshing || group.loading || undefined}
    >
      <div className={styles.groupHeader}>
        <h3 id={headingId} className={styles.groupTitle}>
          {plural}
        </h3>
        {preview && preview.homeSize > 0 ? (
          <span className={styles.muted}>{preview.homeSize.toLocaleString("en-GB")} players</span>
        ) : null}
      </div>

      {group.error && !preview ? <p className={styles.muted}>Preview unavailable</p> : null}
      {group.loading ? <p className={styles.muted}>Loading</p> : null}
      {preview && preview.top.length === 0 ? (
        <p className={styles.muted}>
          {eventOnly ? "No event data for this season" : "No fits for this season"}
        </p>
      ) : null}

      {preview && preview.top.length > 0 ? (
        <DataTable
          label={`Top fits, ${lower}`}
          columns="28px minmax(0, 1.3fr) minmax(0, 1fr) 84px"
          header={["", "Player", "Team", "Percentile"]}
        >
          {preview.top.map((entry, index) => (
            <DataRow key={entry.player.id} className={styles.row}>
              <DataCell className={styles.rank}>{index + 1}</DataCell>
              <DataCell label="Player">
                <EntityName entity={entry.player} />
              </DataCell>
              <DataCell label="Team" className={styles.mutedCell}>
                <EntityName entity={entry.team} />
              </DataCell>
              <DataCell label="Percentile">
                <span
                  className={styles.figure}
                  style={{ color: percentileTextColour(entry.fitPercentile) }}
                >
                  {ordinal(entry.fitPercentile)}
                </span>
              </DataCell>
            </DataRow>
          ))}
        </DataTable>
      ) : null}

      {preview && preview.overlaps.length > 0 ? (
        <DataTable
          label={`Overlap, ${lower}`}
          columns="minmax(0, 1.4fr) 96px minmax(0, 1fr)"
          header={["Archetype", "Spearman r", ""]}
        >
          {preview.overlaps.map((overlap) => (
            <DataRow key={overlap.archetypeId} className={styles.row}>
              <DataCell>
                <Link className={styles.entity} to={`/archetypes/${overlap.archetypeId}`}>
                  {overlap.name || overlap.archetypeId}
                </Link>
              </DataCell>
              <DataCell
                label="Spearman r"
                className={clsx(styles.figure, overlap.flagged && styles.flagged)}
              >
                {formatSpearman(overlap.spearman)}
              </DataCell>
              <DataCell label={overlap.flagged ? "Flag" : undefined} className={styles.flagged}>
                {overlap.flagged ? "Above 0.8" : ""}
              </DataCell>
            </DataRow>
          ))}
        </DataTable>
      ) : null}
    </section>
  );
}

/**
 * A competition search: identity holds every competition, more than a
 * dropdown can list. Interaction as the entity picker's: debounce, arrow
 * keys, Enter to pick, Escape to close.
 */
function CompetitionPicker({
  value,
  loading,
  onSelect,
}: {
  value: PreviewCompetition | null;
  loading: boolean;
  onSelect: (competition: PreviewCompetition) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const ids = useId();
  const inputId = `${ids}-competition`;
  const listboxId = `${ids}-competitions`;
  const debounced = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const searching = editing && debounced.length >= SEARCH_MIN;

  const search = useQuery({
    queryKey: ["archetypes", "preview", "competition-search", debounced],
    queryFn: () => searchEntities({ query: debounced, type: "competition", limit: 10 }),
    enabled: searching,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const results = searching ? (search.data?.results ?? []) : [];
  const open = editing && results.length > 0;

  const close = () => {
    setEditing(false);
    setQuery("");
    setActiveIndex(-1);
  };

  const pick = (index: number) => {
    const entity = results[index];
    if (!entity) return;
    onSelect({ id: entity.entityId, name: entity.name || entity.entityId });
    close();
  };

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        Competition
      </label>
      <div className={styles.combo}>
        <Input
          id={inputId}
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
          }
          placeholder={loading ? "Loading" : undefined}
          value={editing ? query : (value?.name ?? "")}
          onChange={(event) => {
            setEditing(true);
            setQuery(event.currentTarget.value);
            setActiveIndex(-1);
          }}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={close}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
              return;
            }
            if (!open) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((i) => (i + 1) % results.length);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
            } else if (event.key === "Enter" && activeIndex >= 0) {
              event.preventDefault();
              pick(activeIndex);
            }
          }}
        />
        {open ? (
          <ul id={listboxId} role="listbox" aria-label="Competitions" className={styles.listbox}>
            {results.map((entity, index) => (
              <li
                key={entity.entityId}
                id={`${listboxId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={clsx(styles.option, index === activeIndex && styles.optionActive)}
                // Picked on mousedown, before the input's blur closes the list.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(index);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {entity.name || entity.entityId}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {searching && search.isError ? (
        <span className={styles.muted}>Search unavailable</span>
      ) : null}
      {searching && search.isSuccess && results.length === 0 ? (
        <span className={styles.muted}>No competitions match</span>
      ) : null}
    </div>
  );
}
